import { QuestionCircleOutlined } from '@ant-design/icons';
import { Collapse, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CustomTypeOptions } from 'i18next';

interface PageHelpBlock {
  title: string;
  text: string;
}

export type PageHelpNamespace = keyof CustomTypeOptions['resources'];

export interface PageHelpProps {
  /** i18next namespace holding the `pageHelp.<page>` entry (e.g. 'admin', 'auth', 'common'). */
  ns: PageHelpNamespace;
  /** Key of this page's entry under `pageHelp` in that namespace's JSON. */
  page: string;
}

/** Dynamic per-page keys can't be represented in the generated `t()` key union, so this reads
 *  through an unknown-typed accessor instead of widening the shared i18next types with `any`. */
type DynamicT = (key: string, options?: Record<string, unknown>) => unknown;

/** Collapsed-by-default "how this page works" block, rendered at the bottom of every page
 *  (docs/PLAN.md UX note: onboarding via inline help rather than a separate docs site). */
export function PageHelp({ ns, page }: PageHelpProps) {
  const { t } = useTranslation([ns, 'common']);
  const dynamicT = t as unknown as DynamicT;
  const blocks = dynamicT(`pageHelp.${page}.blocks`, {
    ns,
    returnObjects: true,
    defaultValue: [],
  }) as PageHelpBlock[];

  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  return (
    <Collapse
      className="print-hide"
      ghost
      style={{ marginTop: 24 }}
      items={[
        {
          key: 'page-help',
          label: (
            <Flex align="center" gap={8}>
              <QuestionCircleOutlined />
              <Typography.Text type="secondary">
                {t('pageHelp.toggle', { ns: 'common' })}
              </Typography.Text>
            </Flex>
          ),
          children: (
            <Flex vertical gap={12}>
              {blocks.map((block) => (
                <div key={block.title}>
                  <Typography.Text strong>{block.title}</Typography.Text>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
                  >
                    {block.text}
                  </Typography.Paragraph>
                </div>
              ))}
            </Flex>
          ),
        },
      ]}
    />
  );
}
